import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Plus, Trash2, RotateCcw } from 'lucide-react';

const SettingsTerms = () => {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const API_BASE_URL = '/api';

    useEffect(() => {
        loadSettings();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSettings = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('admin_token');
            const { data } = await axios.get(`${API_BASE_URL}/admin/settings`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (data.success) {
                setTerms(data.settings.terms_conditions || []);
            }
        } catch (error) {
            console.error('Error loading terms:', error);
        } finally {
            setLoading(false);
        }
    };

    const save = async () => {
        try {
            const token = localStorage.getItem('admin_token');
            const { data } = await axios.get(`${API_BASE_URL}/admin/settings`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            const settings = data.settings || {};
            settings.terms_conditions = terms;
            await axios.put(`${API_BASE_URL}/admin/settings`, settings, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            alert('Terms saved successfully!');
        } catch (e) {
            console.error('Save error:', e);
            alert('Failed to save terms');
        }
    };

    const reset = async () => {
        if (!window.confirm('Reset to default terms?')) return;
        try {
            const token = localStorage.getItem('admin_token');
            // To strictly reset only terms, we might need a more granular API or just fetch defaults
            // For now, we'll re-fetch with ?defaults=1 to get the default array
            const { data } = await axios.get(`${API_BASE_URL}/admin/settings?defaults=1`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (data.success) {
                setTerms(data.settings.terms_conditions || []);
                alert('Reset to defaults');
            }
        } catch (e) {
            alert('Failed to reset');
        }
    };

    const addTerm = () => {
        setTerms([...terms, "New policy point..."]);
    };

    const updateTerm = (index, value) => {
        const updated = [...terms];
        updated[index] = value;
        setTerms(updated);
    };

    const deleteTerm = (index) => {
        if (!window.confirm('Delete this point?')) return;
        const updated = [...terms];
        updated.splice(index, 1);
        setTerms(updated);
    };

    if (loading) return <div className="text-gray-500 p-4">Loading terms...</div>;

    return (
        <div className="space-y-6">
            {/* Editor */}
            <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
                            <FileText className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="text-xl font-bold text-gray-800">Booking Terms</h4>
                            <p className="text-sm text-gray-500 mt-0.5">{terms.length} points displayed to customers</p>
                        </div>
                    </div>
                    <button
                        onClick={addTerm}
                        className="px-6 py-2.5 rounded-xl text-white font-bold shadow-lg shadow-[#471F21]/20 hover:shadow-xl flex items-center gap-2 transform active:scale-95 transition-all text-sm"
                        style={{ backgroundColor: '#471F21' }}
                    >
                        <Plus className="w-5 h-5" />
                        Add Policy Point
                    </button>
                </div>

                <div className="p-8 space-y-4">
                    {terms.map((term, i) => (
                        <div key={i} className="flex gap-4 items-start group relative">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 border border-gray-100 text-gray-400 flex items-center justify-center font-bold text-[10px] mt-2 transition-all group-focus-within:bg-[#471F21] group-focus-within:text-white group-focus-within:border-[#471F21]">
                                {String(i + 1).padStart(2, '0')}
                            </div>
                            <div className="flex-1 relative">
                                <textarea
                                    className="w-full bg-white border border-gray-200 focus:border-[#471F21] focus:ring-0 rounded-2xl px-5 py-3 text-sm text-gray-700 font-medium leading-relaxed outline-none transition-all resize-none shadow-sm min-h-[80px]"
                                    value={term}
                                    onChange={(e) => updateTerm(i, e.target.value)}
                                    placeholder="Enter policy detail..."
                                />
                            </div>
                            <button
                                onClick={() => deleteTerm(i)}
                                className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl shadow-sm opacity-0 group-hover:opacity-100 transition-all absolute top-2 right-2 md:static md:opacity-100"
                                title="Delete point"
                            >
                                <Trash2 className="w-5 h-5" />
                            </button>
                        </div>
                    ))}

                    {terms.length === 0 && (
                        <div className="text-center py-20 border-2 border-dashed border-gray-100 rounded-2xl">
                            <FileText className="w-12 h-12 text-gray-100 mx-auto mb-4" />
                            <h3 className="text-gray-400 font-semibold tracking-wide">NO POLICIES DEFINED</h3>
                            <button onClick={addTerm} className="mt-4 text-[#471F21] text-sm font-bold hover:underline">Add your first term</button>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="bg-gray-50/50 px-8 py-6 flex items-center justify-between border-t border-gray-50 mt-4">
                    <button
                        onClick={reset}
                        className="flex items-center gap-2 text-[10px] text-gray-400 hover:text-red-500 font-bold tracking-widest transition-colors uppercase"
                    >
                        <RotateCcw className="w-4 h-4" />
                        Reset Defaults
                    </button>

                    <div className="flex gap-4">
                        <button
                            onClick={loadSettings}
                            className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-bold hover:bg-white transition-all shadow-sm"
                        >
                            Discard
                        </button>
                        <button
                            onClick={save}
                            className="px-8 py-2.5 rounded-xl text-white text-sm font-bold shadow-lg shadow-[#471F21]/20 hover:shadow-xl transition-all"
                            style={{ background: '#471F21' }}
                        >
                            Save Terms
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsTerms;
